import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Pagination, Spinner, Alert, Badge, Row, Col, Container, Form, Button } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import apiService from '../services/apiService';
import Breadcrumb from '../components/Breadcrumb';

// Get variant for status badges
const getBadgeVariant = (status) => {
  switch (status) {
    case 'OPEN':
      return 'success';
    case 'CLOSED':
      return 'danger';
    case 'PENDING':
      return 'warning';
    default:
      return 'secondary';
  }
};

// Function to format datetime
const formatDateTime = (dateString) => {
    if (!dateString) return null;
    
    try {
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    } catch (error) {
        return dateString;
    }
};

// Get status icon
const getStatusIcon = (status) => {
  switch (status) {
    case 'OPEN':
      return '📂';
    case 'CLOSED':
      return '✅';
    case 'PENDING':
      return '⏳';
    default:
      return '●';
  }
};

// Function to format address from property data
const formatAddress = (propertyData) => {
    if (!propertyData || !Array.isArray(propertyData) || propertyData.length === 0) {
        return 'No address available';
    }
    
    const property = propertyData[0]; // Get first property
    const { address1, address2, city, state, zipcode } = property;
    
    let formattedAddress = '';
    if (address1) formattedAddress += address1;
    if (address2) formattedAddress += `, ${address2}`;
    if (city) formattedAddress += formattedAddress ? `, ${city}` : city;
    if (state) formattedAddress += `, ${state}`;
    if (zipcode) formattedAddress += ` ${zipcode}`;
    
    return formattedAddress || 'Address not available';
};

const ScheduledAppointments = () => {
    const navigate = useNavigate();
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalAppointments, setTotalAppointments] = useState(0);
    const [showFilters, setShowFilters] = useState(false);
    const [selectedFilter, setSelectedFilter] = useState('all');
    const pageSize = 50; // Define page size

    // Filter options for status filtering
    const filterOptions = [
        { value: 'all', label: 'Scheduled Status', description: 'All scheduled appointments' },
        { value: 'SCHEDULED', label: 'Scheduled', description: 'Scheduled appointments' },
        { value: 'RESCHEDULED', label: 'Rescheduled', description: 'Rescheduled appointments' },
        // { value: 'PENDING', label: 'Pending', description: 'Pending appointments' }
    ];

    // Handle filter change
    const handleFilterChange = (filterValue) => {
        setSelectedFilter(filterValue);
        setCurrentPage(1); // Reset to first page when filter changes
    };

    // Function to fetch scheduled appointments with optional status filter
    const fetchScheduledAppointments = useCallback(async (page) => {
        setLoading(true);
        setError(null);
        try {
            // Determine the status filter
            const statusFilter = selectedFilter !== 'all' ? selectedFilter : null;
            const response = await apiService.getAllEstimatedClosings(page, pageSize, statusFilter, '-estimated_closing');
            
            setAppointments(response.data || []);
            setTotalAppointments(response.pagination?.total_count || 0);
            setTotalPages(response.pagination?.total_pages || 1);
        } catch (err) {
            setError('Failed to fetch scheduled appointments.');
            console.error('Error fetching scheduled appointments:', err);
        } finally {
            setLoading(false);
        }
    }, [pageSize, selectedFilter]);

    // Initial data fetch
    useEffect(() => {
        fetchScheduledAppointments(currentPage);
    }, [currentPage, fetchScheduledAppointments]);

    // Refetch data when filter changes
    useEffect(() => {
        fetchScheduledAppointments(1);
        setCurrentPage(1);
    }, [selectedFilter, fetchScheduledAppointments]);

    // Handle page change
    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    // Render professional status badge with enhanced styling
    const renderStatusBadge = (status) => {
        const getStatusConfig = (status) => {
            switch (status) {
                case 'OPEN':
                    return {
                        bg: 'success',
                        icon: '📂',
                        gradient: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
                        shadow: '0 4px 15px rgba(40, 167, 69, 0.3)',
                        pulse: false
                    };
                case 'CLOSED':
                    return {
                        bg: 'danger',
                        icon: '✅',
                        gradient: 'linear-gradient(135deg, #dc3545 0%, #e74c3c 100%)',
                        shadow: '0 4px 15px rgba(220, 53, 69, 0.3)',
                        pulse: false
                    };
                case 'PENDING':
                    return {
                        bg: 'warning',
                        icon: '⏳',
                        gradient: 'linear-gradient(135deg, #ffc107 0%, #fd7e14 100%)',
                        shadow: '0 4px 15px rgba(255, 193, 7, 0.3)',
                        pulse: true
                    };
                default:
                    return {
                        bg: 'secondary',
                        icon: '●',
                        gradient: 'linear-gradient(135deg, #6c757d 0%, #495057 100%)',
                        shadow: '0 4px 15px rgba(108, 117, 125, 0.3)',
                        pulse: false
                    };
            }
        };

        const config = getStatusConfig(status);
        
        return (
            <div 
                className={`status-badge-professional ${config.pulse ? 'status-badge-pulse' : ''}`}
                style={{
                    background: config.gradient,
                    boxShadow: config.shadow,
                    color: 'white',
                    padding: '0.4rem 0.8rem',
                    borderRadius: '8px',
                    fontSize: '0.7rem',
                    fontWeight: '600',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                    border: 'none',
                    textTransform: 'uppercase',
                    letterSpacing: '0.3px',
                    transition: 'all 0.3s ease',
                    cursor: 'default',
                    minWidth: '85px',
                    justifyContent: 'center'
                }}
            >
                <span 
                    className={`status-icon ${config.pulse ? 'status-icon-spin' : ''}`}
                    style={{ 
                        fontSize: '0.9rem',
                        display: 'inline-block'
                    }}
                >
                    {config.icon}
                </span>
                <span>{status}</span>
            </div>
        );
    };

    // Handle row click to navigate to details page
    const handleRowClick = (appointmentId) => {
        // navigate(`/appointments/${appointmentId}`);
        console.log('Navigate to appointment details:', appointmentId);
    };

    const breadcrumbItems = [

        { label: 'Scheduled Appointments', href: null }
    ];

    return (
        <Container fluid className="px-4 py-4" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
            <Breadcrumb items={breadcrumbItems} />
            <style jsx>{`
                .page-title {
                    color: #2c3e50;
                    font-weight: 700;
                    font-size: 2rem;
                    text-shadow: 0 2px 4px rgba(0,0,0,0.1);
                }
                
                .page-subtitle {
                    font-size: 1rem;
                    color: #6c757d;
                }
                
                .stats-summary {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    border-radius: 12px;
                    padding: 1rem 1.5rem;
                    color: white;
                    margin-bottom: 1.5rem;
                    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
                }
                
                .automation-runs-container {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    border-radius: 20px;
                    padding: 2rem;
                    margin-bottom: 2rem;
                    color: white;
                    box-shadow: 0 10px 30px rgba(0,0,0,0.1);
                }
                
                .stats-card {
                    background: rgba(255,255,255,0.1);
                    backdrop-filter: blur(10px);
                    border: 1px solid rgba(255,255,255,0.2);
                    border-radius: 15px;
                    padding: 1.5rem;
                    color: white;
                    transition: transform 0.3s ease;
                }
                
                .stats-card:hover {
                    transform: translateY(-5px);
                }
                
                .main-card {
                    border: none;
                    border-radius: 20px;
                    box-shadow: 0 10px 40px rgba(0,0,0,0.1);
                    overflow: hidden;
                }
                
                .card-header-custom {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    color: white;
                    border: none;
                    padding: 1.5rem 2rem;
                }
                
                .table-modern {
                    width: 100%;
                    margin-bottom: 0;
                }
                
                .table-responsive {
                    overflow-x: auto !important;
                    overflow-y: visible;
                }
                
                .table-modern thead th {
                    background-color: #f8f9fa;
                    border: none;
                    font-weight: 600;
                    color: #495057;
                    padding: 12px 15px;
                    font-size: 0.85rem;
                    text-transform: uppercase;
                    letter-spacing: 0.3px;
                    white-space: nowrap;
                    border-bottom: 2px solid #e9ecef;
                }
                
                .table-modern tbody tr {
                    cursor: pointer;
                    transition: all 0.2s ease;
                }
                
                .table-modern tbody tr:hover {
                    background-color: #f8f9fa;
                }
                
                .table-modern tbody td {
                    padding: 12px 15px;
                    vertical-align: middle;
                    border-bottom: 1px solid #e9ecef;
                }
                
                .loading-container {
                    padding: 4rem 2rem;
                    text-align: center;
                }
                
                .loading-spinner {
                    width: 3rem;
                    height: 3rem;
                    border-width: 0.3rem;
                }
                
                .empty-state {
                    padding: 4rem 2rem;
                    text-align: center;
                    color: #6c757d;
                }
                
                .empty-state-icon {
                    font-size: 4rem;
                    margin-bottom: 1rem;
                    opacity: 0.5;
                }
                
                .pagination-container {
                    background-color: #f8f9fa;
                    padding: 1.5rem 2rem;
                    border-top: 1px solid #e9ecef;
                }
                
                .pagination-info {
                    color: #6c757d;
                    font-weight: 500;
                }
                
                /* Enhanced Filter Animations */
                @keyframes slideDown {
                    from {
                        opacity: 0;
                        transform: translateY(-20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                
                @keyframes pulse {
                    0% {
                        box-shadow: 0 4px 12px rgba(23, 162, 184, 0.3);
                    }
                    50% {
                        box-shadow: 0 6px 20px rgba(23, 162, 184, 0.5);
                    }
                    100% {
                        box-shadow: 0 4px 12px rgba(23, 162, 184, 0.3);
                    }
                }
                
                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                
                .filter-card {
                    animation: slideDown 0.3s ease-out;
                }
                
                .filter-controls {
                    animation: fadeInUp 0.4s ease-out 0.1s both;
                }
                
                .filter-info {
                    animation: fadeInUp 0.4s ease-out 0.2s both;
                }
                
                .filter-stats {
                    animation: fadeInUp 0.4s ease-out 0.3s both;
                }
                
                .stat-item {
                    transition: all 0.3s ease;
                    padding: 0.5rem;
                    border-radius: 8px;
                }
                
                .stat-item:hover {
                    background-color: rgba(0,0,0,0.05);
                    transform: translateY(-2px);
                }
                
                .form-select-modern:hover {
                    border-color: #667eea !important;
                    transform: translateY(-1px);
                }
                
                .info-badge {
                    transition: all 0.3s ease;
                }
                
                .info-badge:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 20px rgba(23, 162, 184, 0.4) !important;
                }
                
                .btn-group .btn {
                    transition: all 0.3s ease;
                }
                
                .btn-group .btn:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                }
                
               
                
                .status-badge-professional {
                    text-transform: uppercase;
                }
                

                
                .date-cell {
                    color: #4a5568;
                    font-size: 0.75rem;
                    font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
                }
                
                .address-cell {
                    color: #4a5568;
                }
            `}</style>
            
            {/* Page Header Section */}
            <Row className="mb-4">
                <Col>
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h2 className="page-title mb-1">Scheduled Appointments</h2>
                            <p className="page-subtitle text-muted mb-0">
                                Monitor your scheduled closing appointments
                            </p>
                        </div>
                        <div className="d-flex align-items-center gap-3">
                            <div className="btn-group" role="group">
                                 <Button 
          variant={showFilters ? "primary" : "outline-primary"}
          onClick={() => setShowFilters(!showFilters)}
          className="d-flex align-items-center gap-2"
        >
          {showFilters ? 'Filters' : 'Filters'}
                    <i className="fas fa-filter"></i>

        </Button>
                            </div>
                        </div>
                    </div>
                </Col>
            </Row>

            {/* Enhanced Professional Filter Section */}
            {showFilters && (
                <Card 
                    className="mb-4 filter-card" 
                    style={{ 
                        border: 'none', 
                        boxShadow: '0 8px 25px rgba(0,0,0,0.12)',
                        borderRadius: '16px',
                        background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                        animation: 'slideDown 0.3s ease-out'
                    }}
                >
                    <Card.Header 
                        style={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '16px 16px 0 0',
                            padding: '1rem 1.5rem'
                        }}
                    >
                        <div className="d-flex align-items-center justify-content-between">
                            <div className="d-flex align-items-center">
                                <i className="fas fa-sliders-h me-2"></i>
                                <h6 className="mb-0 fw-bold">Filters</h6>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <button 
                                    className="btn btn-sm btn-outline-light"
                                    onClick={() => setShowFilters(false)}
                                    style={{ borderRadius: '6px', padding: '0.25rem 0.5rem' }}
                                >
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                        </div>
                    </Card.Header>
                    <Card.Body style={{ padding: '1rem 1rem 2rem 1rem !important' }}>
                        <Row className="align-items-center g-4 mb-2">
                            <Col lg={3}>
                                <div className="filter-controls">
                                    <label className="form-label fw-semibold text-muted mb-2">
                                        Status
                                    </label>
                                    <div className="position-relative">
                                        <Form.Select
                                            value={selectedFilter}
                                            onChange={(e) => handleFilterChange(e.target.value)}
                                            className="form-select-modern"
                                            style={{
                                                border: '2px solid #e9ecef',
                                                borderRadius: '12px',
                                                fontSize: '0.95rem',
                                                padding: '0.75rem 1rem',
                                                background: 'white',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                                                transition: 'all 0.3s ease',
                                                cursor: 'pointer'
                                            }}
                                            onFocus={(e) => {
                                                e.target.style.borderColor = '#667eea';
                                                e.target.style.boxShadow = '0 0 0 0.2rem rgba(102, 126, 234, 0.25)';
                                            }}
                                            onBlur={(e) => {
                                                e.target.style.borderColor = '#e9ecef';
                                                e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)';
                                            }}
                                        >
                                            {filterOptions.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </Form.Select>
                                        <i className="fas fa-chevron-down position-absolute" 
                                           style={{
                                               right: '1rem',
                                               top: '50%',
                                               transform: 'translateY(-50%)',
                                               pointerEvents: 'none',
                                               color: '#6c757d',
                                               fontSize: '0.8rem'
                                           }}>
                                        </i>
                                    </div>
                                </div>
                            </Col>
                            
                        </Row>
                        
                        {/* Filter Statistics */}
                        <Row className="mt-0 pt-3 pb-2" style={{ borderTop: '1px solid #e9ecef', minHeight: '60px' }}>
                            <Col>
                                <div className="d-flex align-items-center justify-content-end flex-wrap gap-3 h-100">
                                    
                                    <div className="filter-actions d-flex align-items-center justify-content-center" style={{ height: '100%' }}>
                                        <button 
                                            className="btn btn-outline-primary btn-sm me-2"
                                            onClick={() => {
                                                setSelectedFilter('all');
                                                setCurrentPage(1);
                                            }}
                                            style={{ 
                                                borderRadius: '8px',
                                                height: '36px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <i className="fas fa-refresh me-1"></i>
                                            Reset
                                        </button>
                                        <button 
                                            className="btn btn-primary btn-sm"
                                            onClick={() => fetchScheduledAppointments(1)}
                                            style={{ 
                                                borderRadius: '8px',
                                                height: '36px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <i className="fas fa-sync-alt me-1"></i>
                                            Refresh
                                        </button>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
            )}

            {/* Statistics Summary Cards */}
            <Row className="mb-4">
                <Col md={3}>
                    <Card className="border-0 shadow-sm h-100" style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white'
                    }}>
                        <Card.Body className="d-flex align-items-center">
                            <div className="flex-grow-1">
                                <div className="text-white-50 small text-uppercase fw-bold mb-1">
                                    Total Appointments
                                </div>
                                <div className="h4 mb-0 fw-bold">
                                    {totalAppointments}
                                </div>
                            </div>
                            <div className="ms-3">
                                <i className="fas fa-calendar-check fa-2x opacity-75"></i>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="border-0 shadow-sm h-100" style={{
                        background: 'linear-gradient(135deg,rgb(184, 101, 156) 0%,rgb(174, 32, 160) 100%)',
                        color: 'white'
                    }}>
                        <Card.Body className="d-flex align-items-center"> 
                            <div className="flex-grow-1">
                                <div className="text-white-50 small text-uppercase fw-bold mb-1">
                                    Rescheduled Appointments
                                </div>
                                <div className="h4 mb-0 fw-bold">
                                    {0}
                                </div>
                            </div>
                            <div className="ms-3">
                                <i className="fas fa-calendar-check fa-2x opacity-75"></i>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Main Content Card */}
            <Card className="main-card">
                <Card.Header className="card-header-custom">
                    <Row className="align-items-center">
                        <Col>
                            <h5 className="mb-0 fw-bold">{filterOptions.find(opt => opt.value === selectedFilter)?.label}</h5>
                        </Col>
                        
                    </Row>
                </Card.Header>
                
                <Card.Body className="p-0">
                    {loading && (
                        <div className="loading-container">
                            <Spinner 
                                animation="border" 
                                variant="primary" 
                                className="loading-spinner mb-3"
                            />
                            <h5 className="text-muted">Loading scheduled appointments...</h5>
                            <p className="text-muted mb-0">Please wait while we fetch the latest data</p>
                        </div>
                    )}
                    
                    {error && (
                        <div className="p-4">
                            <Alert variant="danger" className="border-0 rounded-3">
                                <Alert.Heading className="h6">⚠️ Error Loading Data</Alert.Heading>
                                {error}
                            </Alert>
                        </div>
                    )}
                    
                    {!loading && !error && (
                        <>
                            {appointments.length > 0 ? (
                                <div className="table-responsive">
                                    <Table hover className="table-modern mb-0">
                                        <thead>
                            <tr>
                                <th><i className="fas fa-hashtag me-1"></i>Order ID</th>
                                <th><i className="fas fa-info-circle me-1"></i>Status</th>
                                <th><i className="fas fa-calendar-check me-1"></i>Scheduled Closing</th>
                                <th><i className="fas fa-map-marker-alt me-1"></i>Closing Location</th>
                                <th><i className="fas fa-home me-1"></i>Property Address</th>
                                <th><i className="fas fa-tags me-1"></i>Overview</th>
                            </tr>
                        </thead>
                                        <tbody>
                                            {appointments.map((appointment) => (
                                <tr 
                                    key={appointment.id} 
                                    onClick={() => handleRowClick(appointment.id)}
                                    className="table-row-clickable"
                                >
                                    <td>
                                        <span className="id-badge">{appointment.order_number || appointment.id}</span>
                                    </td>
                                    <td>
                                        <div 
                                            className={`status-badge-professional status-${appointment.status?.toLowerCase()==="open"?"closed":"open"}`}
                                            style={{
                                                color: 'white',
                                                padding: '0.4rem 0.8rem',
                                                borderRadius: '8px',
                                                fontSize: '0.7rem',
                                                fontWeight: '600',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '0.4rem',
                                                border: 'none',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.3px',
                                                minWidth: '85px',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <span style={{ fontSize: '0.9rem' }}>
                                                {appointment.status === 'OPEN' ? '✗' : appointment.status === 'CLOSED' ? '✓' : '●'}
                                            </span>
                                            <span>{appointment.status}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="date-cell">
                                            {(() => {
                                                if (!appointment.estimated_closing) {
                                                    return 'Not Available';
                                                }
                                                const date = new Date(appointment.estimated_closing + 'T00:00:00Z');
                                                if (isNaN(date.getTime())) {
                                                    return 'Not Available';
                                                }
                                                return date.toLocaleDateString('en-US', {
                                                    year: 'numeric',
                                                    month: 'short',
                                                    day: 'numeric',
                                                    timeZone: 'UTC'
                                                });
                                            })()}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="address-cell">
                                            {appointment.closing_address ? (
                                                <span className="text-primarys fw-medium">{appointment.closing_address}</span>
                                            ) : (
                                                <span className="text-muted fst-italic">Not Available</span>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="address-cell">
                                            {appointment.property_addresses && appointment.property_addresses.length > 0 ? (
                                                <div>
                                                    {appointment.property_addresses.map((address, idx) => (
                                                        <div key={idx} className="mb-1">
                                                            <span className="text-success fw-medium">{address}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : appointment.property_data ? (
                                                <span className="text-success fw-medium">{formatAddress(appointment.property_data)}</span>
                                            ) : (
                                                <span className="text-muted fst-italic">Not Available</span>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <div 
                                            className={`app-status-badge app-status-${appointment.app_status?.replace(/_/g, '-') || 'unknown'}`}
                                            style={{
                                                backgroundColor: appointment.app_status === 'refinance' ? '#6f42c1' :
                                                                appointment.app_status?.includes('fsbo') ? '#fd7e14' :
                                                                appointment.app_status?.includes('agent_copies_exists') ? '#198754' :
                                                                appointment.app_status?.includes('no_agent_copies') ? '#dc3545' :
                                                                appointment.app_status?.includes('no_listing_no_selling') ? '#6c757d' :
                                                                '#0d6efd',
                                                color: 'white',
                                                padding: '0.3rem 0.6rem',
                                                borderRadius: '6px',
                                                fontSize: '0.65rem',
                                                fontWeight: '500',
                                                display: 'inline-block',
                                                textAlign: 'center',
                                                textTransform: 'capitalize',
                                                letterSpacing: '0.2px'
                                            }}
                                            title={appointment.app_status ? appointment.app_status.replace(/_/g, ' ').toUpperCase() : 'Unknown'}
                                        >
                                            {appointment.app_status ? 
                                                appointment.app_status.replace(/_/g, ' ').replace(/but/g, '-').trim() 
                                                : 'Unknown'
                                            }
                                        </div>
                                    </td>
                                </tr>
                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            ) : (
                                <div className="empty-state">
                                    <div className="empty-state-icon">📅</div>
                                    <h5 className="mb-2">No Scheduled Appointments Found</h5>
                                    <p className="mb-0">No appointments match your current filter criteria</p>
                                </div>
                            )}
                            
                            {appointments.length > 0 && (
                                <div className="pagination-container">
                                    <Row className="align-items-center">
                                        <Col>
                                            <div className="pagination-info">
                                                {/* Showing <strong>{appointments.length}</strong> of <strong>{totalAppointments}</strong> entries */}
                                            </div>
                                        </Col>
                                        <Col xs="auto">
                                            <Pagination className="mb-0">
                                                <Pagination.First
                                                    onClick={() => handlePageChange(1)}
                                                    disabled={currentPage === 1}
                                                />
                                                <Pagination.Prev 
                                                    onClick={() => handlePageChange(currentPage - 1)} 
                                                    disabled={currentPage === 1}
                                                />
                                                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                                    // Show 5 pages around current page
                                                    let pageNum;
                                                    if (totalPages <= 5) {
                                                        pageNum = i + 1;
                                                    } else if (currentPage <= 3) {
                                                        pageNum = i + 1;
                                                    } else if (currentPage >= totalPages - 2) {
                                                        pageNum = totalPages - 4 + i;
                                                    } else {
                                                        pageNum = currentPage - 2 + i;
                                                    }
                                                    
                                                    return (
                                                        <Pagination.Item 
                                                            key={pageNum} 
                                                            active={pageNum === currentPage}
                                                            onClick={() => handlePageChange(pageNum)}
                                                        >
                                                            {pageNum}
                                                        </Pagination.Item>
                                                    );
                                                })}
                                                <Pagination.Next 
                                                    onClick={() => handlePageChange(currentPage + 1)} 
                                                    disabled={currentPage === totalPages}
                                                />
                                                <Pagination.Last 
                                                    onClick={() => handlePageChange(totalPages)}
                                                    disabled={currentPage === totalPages}
                                                />
                                            </Pagination>
                                        </Col>
                                    </Row>
                                </div>
                            )}
                        </>
                    )}
                </Card.Body>
            </Card>
        </Container>
    );
};

export default ScheduledAppointments;
